<template>
  <div class="politica-form">
    <div
      class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
    >
      <div>
        <h5 class="mb-1">
          {{
            isEditing
              ? 'Editar Política de Cobrança'
              : 'Nova Política de Cobrança'
          }}
        </h5>

        <small class="text-muted">
          Configure a política enquanto ela estiver em rascunho.
        </small>
      </div>

      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        :disabled="saving"
        @click="voltar"
      >
        <i class="bi bi-arrow-left me-1"></i>
        Voltar
      </button>
    </div>

    <div
      v-if="loading"
      class="card card-body shadow-sm py-5 text-center"
    >
      <div>
        <div
          class="spinner-border spinner-border-sm text-primary"
          role="status"
        ></div>
      </div>

      <div class="mt-2 text-muted">
        Carregando política...
      </div>
    </div>

    <div
      v-else-if="permissionError"
      class="alert alert-danger"
      role="alert"
    >
      {{ permissionError }}
    </div>

    <form
      v-else
      class="card shadow-sm policy-card"
      @submit.prevent="salvar"
    >
      <div
        v-if="errorMessage"
        class="alert alert-danger"
        role="alert"
      >
        {{ errorMessage }}

        <button
          v-if="concurrencyConflict && isEditing"
          type="button"
          class="btn btn-outline-danger btn-sm ms-2"
          :disabled="saving"
          @click="recarregarAposConflito"
        >
          Recarregar dados
        </button>
      </div>

      <FormSection title="Identificação e vigência">
        <div class="policy-grid identification-grid">
          <div class="policy-field">
            <label
              for="politica-nome"
              class="form-label"
            >
              Nome <span class="text-danger">*</span>
            </label>

            <input
              id="politica-nome"
              v-model.trim="form.nome"
              type="text"
              maxlength="150"
              class="form-control"
              required
            />
          </div>

          <div class="policy-field">
            <label
              for="politica-inicio"
              class="form-label"
            >
              Início da vigência
              <span class="text-danger">*</span>
            </label>

            <input
              id="politica-inicio"
              v-model="form.vigencia_inicio"
              type="date"
              class="form-control"
              required
            />
          </div>

          <div class="policy-field">
            <label
              for="politica-fim"
              class="form-label"
            >
              Fim da vigência
            </label>

            <input
              id="politica-fim"
              v-model="form.vigencia_fim"
              type="date"
              class="form-control"
            />
          </div>
        </div>
      </FormSection>

      <FormSection title="Dias regulares de vencimento">
        <div class="days-selection">
          <div class="days-grid">
            <div
              v-for="dia in diasDisponiveis"
              :key="dia"
              class="day-option"
              :class="{
                'day-selected':
                  isDiaSelecionado(dia)
              }"
            >
              <label
                class="form-check-label d-flex align-items-center gap-2"
                :for="`dia-${dia}`"
              >
                <input
                  :id="`dia-${dia}`"
                  type="checkbox"
                  class="form-check-input mt-0"
                  :checked="isDiaSelecionado(dia)"
                  @change="toggleDia(dia, $event.target.checked)"
                />

                <strong>
                  {{ dia }}
                </strong>
              </label>

              <label
                v-if="isDiaSelecionado(dia)"
                class="default-day"
                :for="`padrao-${dia}`"
              >
                <input
                  :id="`padrao-${dia}`"
                  v-model="diaPadrao"
                  type="radio"
                  name="dia-padrao"
                  :value="dia"
                  class="form-check-input mt-0"
                />

                padrão
              </label>
            </div>
          </div>
          <p class="form-text text-muted days-help">
            Escolha os dias de vencimento (1 a 28). Selecione ao menos um para publicar.
          </p>
        </div>
      </FormSection>

      <FormSection title="Pró-rata">
        <div class="policy-grid option-grid">
          <div class="policy-field">
            <div class="form-check form-switch policy-switch">
              <input
                id="prorata-habilitado"
                v-model="form.prorata_habilitado"
                type="checkbox"
                class="form-check-input"
              />

              <label
                for="prorata-habilitado"
                class="form-check-label"
              >
                Habilitar pró-rata
              </label>
            </div>
          </div>

          <div class="policy-field">
            <label
              for="prorata-modo"
              class="form-label"
            >
              Modo
            </label>

            <select
              id="prorata-modo"
              v-model="form.prorata_modo"
              class="form-select"
              :disabled="!form.prorata_habilitado"
              :required="form.prorata_habilitado"
            >
              <option value="">
                Selecione
              </option>

              <option value="ACUMULAR_PROXIMA_PARCELA">
                Somar à próxima parcela regular
              </option>

              <option value="PRIMEIRA_PARCELA">
                Parcela inicial só de pró-rata
              </option>
            </select>
          </div>
        </div>
      </FormSection>

      <FormSection title="Desconto por antecipação">
        <div class="policy-grid discount-grid">
          <div class="policy-field">
            <div class="form-check form-switch policy-switch">
              <input
                id="desconto-habilitado"
                v-model="form.desconto_antecipacao_habilitado"
                type="checkbox"
                class="form-check-input"
              />

              <label
                for="desconto-habilitado"
                class="form-check-label"
              >
                Habilitar desconto
              </label>
            </div>
          </div>

          <div class="policy-field">
            <label
              for="desconto-percentual"
              class="form-label"
            >
              Percentual (%)
            </label>

            <input
              id="desconto-percentual"
              v-model.trim="form.desconto_antecipacao_percentual"
              type="text"
              inputmode="decimal"
              class="form-control"
              placeholder="Ex.: 5 ou 5,5"
              :disabled="!form.desconto_antecipacao_habilitado"
              :required="form.desconto_antecipacao_habilitado"
            />
          </div>

          <div class="policy-field">
            <label
              for="desconto-dias"
              class="form-label"
            >
              Dias antes do vencimento
            </label>

            <input
              id="desconto-dias"
              v-model.trim="form.desconto_antecipacao_dias"
              type="number"
              min="0"
              step="1"
              class="form-control"
              :disabled="!form.desconto_antecipacao_habilitado"
              :required="form.desconto_antecipacao_habilitado"
            />
          </div>

          <p class="form-text discount-help">
            Use 0 para permitir o desconto até o dia do vencimento.
          </p>
        </div>
      </FormSection>

      <FormSection title="Juros diários — SICREDI">
        <div class="daily-interest-field">
          <label for="sicredi-juros-dia" class="form-label">Valor por dia (R$)</label>
          <input
            id="sicredi-juros-dia"
            v-model.trim="form.sicredi_juros_valor_dia"
            type="text"
            inputmode="decimal"
            class="form-control"
            placeholder="Ex.: 0,50"
            aria-describedby="sicredi-juros-ajuda sicredi-juros-inicio"
          />
        </div>
        <p id="sicredi-juros-ajuda" class="form-text mt-2">
          Opcional. Para novos boletos tradicionais ou híbridos (boleto + Pix) SICREDI. Não combine com multa ou juros mensais.
        </p>
        <p id="sicredi-juros-inicio" class="form-text mt-1">
          Não é possível definir a data de início dos juros nesta integração.
        </p>
      </FormSection>

      <FormSection title="Mora percentual diária — SICREDI">
        <label for="sicredi-percentual-dia" class="form-label">Percentual por dia (%)</label>
        <input id="sicredi-percentual-dia" v-model.trim="form.sicredi_juros_percentual_dia"
          type="text" inputmode="decimal" class="form-control" placeholder="Ex.: 0,03" />
        <p class="form-text">Opcional. Pode ser combinado com a multa percentual abaixo. Não combine com R$/dia ou juros mensais.</p>
        <p class="form-text">As taxas são enviadas sem datas de início. Início da incidência, arredondamento e validade do Pix após vencimento dependem de confirmação bancária.</p>
      </FormSection>
      <div class="legacy-charges">
        <p class="text-muted small legacy-help">
          No SICREDI, a multa abaixo está disponível com a nova mora percentual diária. Juros mensais mantêm o contrato anterior e não estão disponíveis para emissão nesta integração.
        </p>
        <div class="charges-grid">
          <FormSection title="Multa por atraso">
            <div class="policy-grid option-grid">
              <div class="policy-field">
                <div class="form-check form-switch policy-switch">
                  <input
                    id="multa-habilitada"
                    v-model="form.multa_habilitada"
                    type="checkbox"
                    class="form-check-input"
                  />

                  <label
                    for="multa-habilitada"
                    class="form-check-label"
                  >
                    Habilitar multa
                  </label>
                </div>
              </div>

              <div class="policy-field">
                <label
                  for="multa-percentual"
                  class="form-label"
                >
                  Percentual (%)
                </label>

                <input
                  id="multa-percentual"
                  v-model.trim="form.multa_percentual"
                  type="text"
                  inputmode="decimal"
                  class="form-control"
                  placeholder="Ex.: 2"
                  :disabled="!form.multa_habilitada"
                  :required="form.multa_habilitada"
                />
              </div>
            </div>
          </FormSection>

          <FormSection title="Juros mensais por atraso">
            <div class="policy-grid option-grid">
              <div class="policy-field">
                <div class="form-check form-switch policy-switch">
                  <input
                    id="juros-habilitado"
                    v-model="form.juros_habilitado"
                    type="checkbox"
                    class="form-check-input"
                  />

                  <label
                    for="juros-habilitado"
                    class="form-check-label"
                  >
                    Habilitar juros mensais
                  </label>
                </div>
              </div>

              <div class="policy-field">
                <label
                  for="juros-percentual"
                  class="form-label"
                >
                  Percentual ao mês (%)
                </label>

                <input
                  id="juros-percentual"
                  v-model.trim="form.juros_percentual_mes"
                  type="text"
                  inputmode="decimal"
                  class="form-control"
                  placeholder="Ex.: 1"
                  :disabled="!form.juros_habilitado"
                  :required="form.juros_habilitado"
                />
              </div>
            </div>
          </FormSection>

        </div>
      </div>

      <div class="card-footer policy-actions">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="saving"
          @click="voltar"
        >
          Cancelar
        </button>

        <button
          type="submit"
          class="btn btn-primary btn-sm"
          :disabled="saving || !formValido"
        >
          <span
            v-if="saving"
            class="spinner-border spinner-border-sm me-1"
            role="status"
            aria-hidden="true"
          ></span>

          {{
            saving
              ? 'Salvando...'
              : 'Salvar rascunho'
          }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  reactive,
  ref
} from 'vue';

import {
  useRoute,
  useRouter
} from 'vue-router';

import FormSection
  from '@/components/Layout/FormSection.vue';

import {
  usePermissions
} from '@/composables/usePermissions';

import politicasCobrancasApi
  from '@/services/politicasCobrancas.api';

const route =
  useRoute();

const router =
  useRouter();

const {
  fetchPermissions,
  hasPermission
} = usePermissions();

const loading =
  ref(false);

const saving =
  ref(false);

const errorMessage =
  ref('');

const permissionError =
  ref('');

const concurrencyConflict =
  ref(false);

const expectedAtualizadoEm =
  ref('');

const selectedDays =
  ref([]);

const diaPadrao =
  ref(null);

const diasDisponiveis =
  Array.from(
    {
      length: 28
    },
    (
      _,
      index
    ) =>
      index + 1
  );

const form =
  reactive({
    nome: '',
    vigencia_inicio: '',
    vigencia_fim: '',

    prorata_habilitado:
      true,

    prorata_modo:
      'ACUMULAR_PROXIMA_PARCELA',

    desconto_antecipacao_habilitado:
      false,

    desconto_antecipacao_percentual:
      '',

    desconto_antecipacao_dias:
      '0',

    multa_habilitada:
      false,

    sicredi_juros_valor_dia: '',
    sicredi_juros_percentual_dia: '',

    multa_percentual:
      '',

    juros_habilitado:
      false,

    juros_percentual_mes:
      ''
  });

const routeId =
  computed(() => {
    if (
      route.name !==
        'EditarPoliticaCobranca'
    ) {
      return null;
    }

    const id =
      Number(
        route.params.id
      );

    return (
      Number.isSafeInteger(id) &&
      id > 0
    )
      ? id
      : null;
  });

const isEditing =
  computed(() =>
    routeId.value !==
      null
  );

function decimalInputValido(
  value
) {
  const raw =
    String(value ?? '')
      .trim();

  if (
    !/^\d+(?:[.,]\d+)?$/.test(
      raw
    )
  ) {
    return false;
  }

  return (
    raw
      .replace(/[.,]/g, '')
      .replace(/0/g, '')
      .length > 0
  );
}

function integerInputValido(
  value
) {
  return /^\d+$/.test(
    String(value ?? '')
      .trim()
  );
}

const formValido =
  computed(() => {
    if (
      !form.nome.trim() ||
      !/^\d{4}-\d{2}-\d{2}$/.test(
        form.vigencia_inicio
      )
    ) {
      return false;
    }

    if (
      form.vigencia_fim &&
      !/^\d{4}-\d{2}-\d{2}$/.test(
        form.vigencia_fim
      )
    ) {
      return false;
    }

    if (
      form.prorata_habilitado &&
      ![
        'ACUMULAR_PROXIMA_PARCELA',
        'PRIMEIRA_PARCELA'
      ].includes(
        form.prorata_modo
      )
    ) {
      return false;
    }

    if (
      form.desconto_antecipacao_habilitado &&
      (
        !decimalInputValido(
          form.desconto_antecipacao_percentual
        ) ||
        !integerInputValido(
          form.desconto_antecipacao_dias
        )
      )
    ) {
      return false;
    }

    if (
      form.multa_habilitada &&
      !decimalInputValido(
        form.multa_percentual
      )
    ) {
      return false;
    }

    if (
      form.juros_habilitado &&
      !decimalInputValido(
        form.juros_percentual_mes
      )
    ) {
      return false;
    }

    if (form.sicredi_juros_percentual_dia &&
        (form.juros_habilitado || form.sicredi_juros_valor_dia ||
         !/^(?:0|[1-9]\d?)(?:[.,]\d{1,2})?$/.test(form.sicredi_juros_percentual_dia) ||
         Number(form.sicredi_juros_percentual_dia.replace(',', '.')) <= 0 ||
         (form.multa_habilitada && (!/^\d{1,3}(?:[.,]\d{1,2}0{0,4})?$/.test(String(form.multa_percentual)) || Number(String(form.multa_percentual).replace(',', '.')) <= 0)))) return false;
    if (form.sicredi_juros_valor_dia &&
        (form.juros_habilitado || form.multa_habilitada ||
         !/^(?:0|[1-9]\d{0,9})(?:[.,]\d{1,2})?$/.test(form.sicredi_juros_valor_dia) ||
         Number(form.sicredi_juros_valor_dia.replace(',', '.')) <= 0)) return false;
    return true;
  });

function normalizeDecimalString(
  value
) {
  return String(value ?? '')
    .trim()
    .replace(',', '.');
}

function normalizeInteger(
  value
) {
  const raw =
    String(value ?? '')
      .trim();

  if (
    !/^\d+$/.test(raw)
  ) {
    throw new TypeError(
      'Valor inteiro inválido.'
    );
  }

  const result =
    Number(raw);

  if (
    !Number.isSafeInteger(
      result
    ) ||
    result < 0
  ) {
    throw new TypeError(
      'Valor inteiro inválido.'
    );
  }

  return result;
}

function isDiaSelecionado(
  dia
) {
  return selectedDays.value
    .includes(dia);
}

function toggleDia(
  dia,
  checked
) {
  if (checked) {
    if (
      !selectedDays.value
        .includes(dia)
    ) {
      selectedDays.value = [
        ...selectedDays.value,
        dia
      ].sort(
        (a, b) =>
          a - b
      );
    }

    return;
  }

  selectedDays.value =
    selectedDays.value
      .filter(
        (item) =>
          item !== dia
      );

  if (
    diaPadrao.value ===
      dia
  ) {
    diaPadrao.value =
      null;
  }
}

function montarPayload() {
  return {
    nome:
      form.nome.trim(),

    vigencia_inicio:
      form.vigencia_inicio,

    vigencia_fim:
      form.vigencia_fim ||
      null,

    prorata_habilitado:
      Boolean(
        form.prorata_habilitado
      ),

    prorata_modo:
      form.prorata_habilitado
        ? form.prorata_modo
        : null,

    desconto_antecipacao_habilitado:
      Boolean(
        form
          .desconto_antecipacao_habilitado
      ),

    desconto_antecipacao_percentual:
      form
        .desconto_antecipacao_habilitado
        ? normalizeDecimalString(
            form
              .desconto_antecipacao_percentual
          )
        : null,

    desconto_antecipacao_dias:
      form
        .desconto_antecipacao_habilitado
        ? normalizeInteger(
            form
              .desconto_antecipacao_dias
          )
        : null,

    sicredi_juros_percentual_dia: form.sicredi_juros_percentual_dia
      ? normalizeDecimalString(form.sicredi_juros_percentual_dia) : null,
    sicredi_juros_valor_dia: form.sicredi_juros_valor_dia
      ? normalizeDecimalString(form.sicredi_juros_valor_dia) : null,

    multa_habilitada:
      Boolean(
        form.multa_habilitada
      ),

    multa_percentual:
      form.multa_habilitada
        ? normalizeDecimalString(
            form.multa_percentual
          )
        : null,

    juros_habilitado:
      Boolean(
        form.juros_habilitado
      ),

    juros_percentual_mes:
      form.juros_habilitado
        ? normalizeDecimalString(
            form.juros_percentual_mes
          )
        : null,

    dias_vencimento:
      [
        ...selectedDays.value
      ]
        .sort(
          (a, b) =>
            a - b
        )
        .map(
          (dia) => ({
            dia,

            padrao:
              diaPadrao.value ===
              dia
          })
        )
  };
}

function hydrate(
  politica
) {
  form.nome =
    String(
      politica?.nome ?? ''
    );

  form.vigencia_inicio =
    String(
      politica?.vigencia_inicio ??
      ''
    );

  form.vigencia_fim =
    String(
      politica?.vigencia_fim ??
      ''
    );

  form.prorata_habilitado =
    Boolean(
      politica
        ?.prorata_habilitado
    );

  form.prorata_modo =
    String(
      politica?.prorata_modo ??
      ''
    );

  form.desconto_antecipacao_habilitado =
    Boolean(
      politica
        ?.desconto_antecipacao_habilitado
    );

  form.desconto_antecipacao_percentual =
    politica
      ?.desconto_antecipacao_percentual ??
    '';

  form.desconto_antecipacao_dias =
    politica
      ?.desconto_antecipacao_dias ??
    '0';

  form.multa_habilitada =
    Boolean(
      politica?.multa_habilitada
    );

  form.sicredi_juros_percentual_dia = politica?.sicredi_juros_percentual_dia ?? '';
  form.sicredi_juros_valor_dia = politica?.sicredi_juros_valor_dia ?? '';

  form.multa_percentual =
    politica?.multa_percentual ??
    '';

  form.juros_habilitado =
    Boolean(
      politica?.juros_habilitado
    );

  form.juros_percentual_mes =
    politica?.juros_percentual_mes ??
    '';

  const dias =
    Array.isArray(
      politica?.dias_vencimento
    )
      ? politica.dias_vencimento
      : [];

  selectedDays.value =
    dias
      .map(
        (item) =>
          Number(item?.dia)
      )
      .filter(
        (dia) =>
          Number.isInteger(dia) &&
          dia >= 1 &&
          dia <= 28
      )
      .filter(
        (
          dia,
          index,
          array
        ) =>
          array.indexOf(dia) ===
          index
      )
      .sort(
        (a, b) =>
          a - b
      );

  const padrao =
    dias.find(
      (item) =>
        item?.padrao ===
        true
    );

  diaPadrao.value =
    padrao
      ? Number(padrao.dia)
      : null;

  expectedAtualizadoEm.value =
    String(
      politica
        ?.atualizado_em_token ??
      ''
    );
}

function apiErrorMessage(
  error,
  fallback
) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    fallback
  );
}

async function carregarEdicao() {
  if (
    !routeId.value
  ) {
    return;
  }

  loading.value =
    true;

  errorMessage.value =
    '';

  concurrencyConflict.value =
    false;

  try {
    const result =
      await politicasCobrancasApi
        .detalhar(
          routeId.value
        );

    const politica =
      result?.politica;

    if (
      !politica ||
      typeof politica !==
        'object'
    ) {
      throw new Error(
        'Resposta inválida do serviço de políticas.'
      );
    }

    if (
      politica.situacao !==
        'RASCUNHO'
    ) {
      errorMessage.value =
        'Somente políticas em rascunho podem ser editadas.';

      return;
    }

    hydrate(
      politica
    );
  } catch (error) {
    console.error(
      'Erro ao carregar política para edição:',
      error
    );

    errorMessage.value =
      apiErrorMessage(
        error,
        'Não foi possível carregar a política para edição.'
      );
  } finally {
    loading.value =
      false;
  }
}

async function salvar() {
  if (
    saving.value ||
    !formValido.value
  ) {
    return;
  }

  saving.value =
    true;

  errorMessage.value =
    '';

  concurrencyConflict.value =
    false;

  try {
    const payload =
      montarPayload();

    const result =
      isEditing.value
        ? await politicasCobrancasApi
            .atualizar(
              routeId.value,
              payload,
              expectedAtualizadoEm.value
            )
        : await politicasCobrancasApi
            .criar(
              payload
            );

    const politica =
      result?.politica;

    if (
      !politica?.id
    ) {
      throw new Error(
        'Resposta inválida ao salvar política.'
      );
    }

    await router.push({
      name:
        'DetalharPoliticaCobranca',

      params: {
        id:
          politica.id
      }
    });
  } catch (error) {
    console.error(
      'Erro ao salvar política de cobrança:',
      error
    );

    if (
      error?.response?.status ===
        409
    ) {
      concurrencyConflict.value =
        true;

      errorMessage.value =
        'Esta política foi alterada por outro usuário. Recarregue os dados antes de tentar salvar novamente.';

      return;
    }

    errorMessage.value =
      apiErrorMessage(
        error,
        'Não foi possível salvar a política de cobrança.'
      );
  } finally {
    saving.value =
      false;
  }
}

async function recarregarAposConflito() {
  await carregarEdicao();
}

function voltar() {
  if (
    isEditing.value
  ) {
    router.push({
      name:
        'DetalharPoliticaCobranca',

      params: {
        id:
          routeId.value
      }
    });

    return;
  }

  router.push({
    name:
      'ListarPoliticasCobrancas'
  });
}

onMounted(
  async () => {
    await fetchPermissions();

    if (
      !hasPermission(
        'financeiro_cobrancas_configurar'
      )
    ) {
      permissionError.value =
        'Você não possui permissão para configurar políticas de cobrança.';

      return;
    }

    if (
      route.name ===
        'EditarPoliticaCobranca' &&
      !routeId.value
    ) {
      permissionError.value =
        'Identificador da política de cobrança inválido.';

      return;
    }

    await carregarEdicao();
  }
);
</script>

<style scoped>
.politica-form { width: 100%; container-type: inline-size; }
.policy-card { max-width: none; padding: 0; gap: 0; overflow: hidden; }
.policy-card :deep(.form-section) {
  margin: 0;
  padding: 1.25rem 1.5rem;
  background: transparent;
  border: 0;
  border-bottom: 1px solid #dee2e6;
  border-radius: 0;
}
.policy-card :deep(.form-section-title) { font-size: 1rem; font-weight: 500; margin-bottom: .75rem; }
.policy-card :deep(.form-section-content) { line-height: 1.5; }
.policy-grid { display: grid; gap: 1rem; align-items: end; }
.identification-grid { grid-template-columns: minmax(0, 2fr) repeat(2, minmax(0, 1fr)); }
.option-grid { grid-template-columns: minmax(12rem, 1fr) minmax(0, 2fr); }
.discount-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.policy-field { min-width: 0; }
.form-label { font-size: .875rem; font-weight: 600; margin-bottom: .375rem; }
.form-control, .form-select { height: auto; min-height: 2.375rem; font-size: .875rem; padding: .375rem .75rem; }
.form-select { padding-right: 2rem; }
.policy-switch { display: flex; align-items: center; gap: .625rem; min-height: 2.375rem; padding-left: 0; margin: 0; }
.policy-switch .form-check-input { float: none; flex: 0 0 2em; margin: 0; }
.policy-switch .form-check-label { margin: 0; font-size: .875rem; line-height: 1.4; }
.form-text { font-size: .8125rem; line-height: 1.5; }
.discount-help { grid-column: 1 / -1; margin: 0; }
.daily-interest-field { width: 100%; max-width: 22rem; }
.legacy-charges { padding: 1.25rem 1.5rem; }
.legacy-help { margin: 0 0 1rem; line-height: 1.5; }
.charges-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 25rem), 1fr)); gap: 1.5rem; }
.charges-grid :deep(.form-section) { padding: 0; border: 0; }
.charges-grid .option-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.policy-actions { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: .5rem; padding: 1rem 1.5rem; }
.days-selection { width: 100%; min-width: 0; }
.days-help { margin: .75rem 0 0; }
.days-grid { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: .5rem; }
.day-option { display: grid; grid-template-rows: repeat(2, 1.625rem); row-gap: .375rem; min-width: 0; border: 1px solid #dee2e6; border-radius: 4px; background: #fff; padding: .5rem; }
.day-option label { margin-bottom: 0; cursor: pointer; }
.day-option .form-check-input { flex-shrink: 0; }
.day-selected { border-color: #86b7fe; background: #f4f8ff; }
.default-day { display: flex; align-items: center; gap: .25rem; margin-top: 0; color: #6c757d; font-size: .6875rem; }
@container (max-width: 800px) {
  .identification-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .identification-grid > :first-child { grid-column: 1 / -1; }
  .discount-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .discount-grid > :first-child { grid-column: 1 / -1; }
}
@container (max-width: 600px) {
  .policy-card :deep(.form-section), .legacy-charges { padding: 1rem; }
  .policy-grid, .charges-grid .option-grid { grid-template-columns: minmax(0, 1fr); }
  .days-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: .375rem; }
  .day-option { padding: .375rem; }
  .charges-grid :deep(.form-section) { padding: 0; }
  .policy-actions { padding: 1rem; }
  .policy-actions .btn { flex: 1 1 auto; }
}
@container (max-width: 340px) {
  .days-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
</style>
